// ===========================================
// Page Header Component
// ===========================================
// Consistent page header with title, subtitle, and action button

import { useContext } from 'react';
import { Box, Typography, Button, Breadcrumbs, Link } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { AuthContext } from '../../contexts/AuthContext.types';
import { resolveCrumbs } from './breadcrumbRoot';

// ===========================================
// TYPES
// ===========================================

export interface BreadcrumbItem {
  label: string;
  path?: string;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  actionLabel?: string;
  actionIcon?: React.ReactNode;
  onAction?: () => void;
  action?: React.ReactNode; // Custom action content (overrides actionLabel/onAction)
}

// ===========================================
// COMPONENT
// ===========================================

/**
 * The crumbs, resolved for where the user is (#434): the root crumb is the CURRENT view, and every
 * view path carries the school slug. Its own component because it reads the location, which only
 * a page with crumbs (and so with links) needs a router for.
 */
function PageBreadcrumbs({ crumbs }: { crumbs: BreadcrumbItem[] }) {
  const { schoolSlug } = useTenant();
  const { pathname } = useLocation();
  // Not `useAuth`: a header must render outside an AuthProvider too. Only the /admin root's label
  // (Admin or Staff) reads the user.
  const user = useContext(AuthContext)?.user;
  return (
    <Breadcrumbs sx={{ mb: 1 }}>
      {resolveCrumbs(crumbs, pathname, schoolSlug, user).map((crumb, index) =>
        crumb.path ? (
          <Link key={index} component={RouterLink} to={crumb.path} color="inherit" underline="hover">
            {crumb.label}
          </Link>
        ) : (
          <Typography key={index} color="text.primary">
            {crumb.label}
          </Typography>
        )
      )}
    </Breadcrumbs>
  );
}

export default function PageHeader({
  title,
  subtitle,
  icon,
  breadcrumbs,
  actionLabel,
  actionIcon = <AddIcon />,
  onAction,
  action,
}: PageHeaderProps) {
  return (
    <Box sx={{ mb: 3 }}>
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && <PageBreadcrumbs crumbs={breadcrumbs} />}

      {/* Title and Action. On a phone the actions STACK below the title at full width
          (2026-09-30): side by side, the title was squeezed into a narrow column and every
          button label wrapped over two or three lines, pushing the last one off-screen. */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: subtitle ? 1 : 0 }}>
            {icon}
            <Typography variant="h4" component="h1">
              {title}
            </Typography>
          </Box>
          {subtitle && (
            <Typography variant="body1" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {action ? (
          action
        ) : (
          actionLabel && onAction && (
            <Button
              variant="contained"
              startIcon={actionIcon}
              onClick={onAction}
            >
              {actionLabel}
            </Button>
          )
        )}
      </Box>
    </Box>
  );
}
